import { useEffect, useRef } from 'react';

interface UseNotificationRefetchOptions<TNotification> {
	notification: TNotification | null | undefined;
	getKey: (notification: TNotification) => string;
	shouldRefetch: (notification: TNotification) => boolean;
	refetch: () => void | Promise<unknown>;
	delayMs?: number;
}

export default function useNotificationRefetch<TNotification>({
	notification,
	getKey,
	shouldRefetch,
	refetch,
	delayMs = 250,
}: UseNotificationRefetchOptions<TNotification>) {
	const lastKeyRef = useRef<string | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const refetchRef = useRef(refetch);
	const getKeyRef = useRef(getKey);
	const shouldRefetchRef = useRef(shouldRefetch);

	useEffect(() => {
		refetchRef.current = refetch;
	}, [refetch]);

	useEffect(() => {
		getKeyRef.current = getKey;
	}, [getKey]);

	useEffect(() => {
		shouldRefetchRef.current = shouldRefetch;
	}, [shouldRefetch]);

	useEffect(() => {
		if (!notification || !shouldRefetchRef.current(notification)) {
			return;
		}

		const key = getKeyRef.current(notification);
		if (lastKeyRef.current === key) {
			return;
		}

		lastKeyRef.current = key;

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			void refetchRef.current();
			timeoutRef.current = null;
		}, delayMs);
	}, [delayMs, notification]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, []);
}
